import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

interface TagProps {
    label: string;
    onRemove?: () => void;
    className?: string;
    color?: 'default' | 'blue' | 'green' | 'red' | 'amber';
}

const colorClasses = {
    default: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
};

export function Tag({ label, onRemove, className, color = 'default' }: TagProps) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
                colorClasses[color],
                className,
            )}
        >
            {label}
            {onRemove && (
                <button
                    onClick={onRemove}
                    className="ml-0.5 rounded-full hover:bg-black/10"
                >
                    <X size={12} />
                </button>
            )}
        </span>
    );
}

interface TagInputProps {
    tags: string[];
    onAdd: (tag: string) => void;
    onRemove: (index: number) => void;
    placeholder?: string;
    label?: string;
}

export function TagInput({ tags, onAdd, onRemove, placeholder = '输入后回车添加', label }: TagInputProps) {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const value = (e.target as HTMLInputElement).value.trim();
            if (value && !tags.includes(value)) {
                onAdd(value);
                (e.target as HTMLInputElement).value = '';
            }
        }
    };

    return (
        <div className="flex flex-col gap-1">
            {label && <label className="text-xs font-medium text-zinc-600">{label}</label>}
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-2 py-1.5 min-h-9">
                {tags.map((tag, index) => (
                    <Tag key={`${tag}-${index}`} label={tag} onRemove={() => onRemove(index)} color="blue" />
                ))}
                <input
                    type="text"
                    onKeyDown={handleKeyDown}
                    placeholder={tags.length === 0 ? placeholder : ''}
                    className="flex-1 min-w-[100px] border-none bg-transparent text-sm outline-none placeholder:text-zinc-400"
                />
            </div>
        </div>
    );
}

interface BadgeProps {
    children: ReactNode;
    variant?: 'default' | 'success' | 'warning' | 'error';
    className?: string;
}

export function Badge({ children, variant = 'default', className }: BadgeProps) {
    const variants = {
        default: 'bg-zinc-100 text-zinc-600',
        success: 'bg-green-100 text-green-700',
        warning: 'bg-amber-100 text-amber-700',
        error: 'bg-red-100 text-red-700',
    };
    return (
        <span className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium', variants[variant], className)}>
            {children}
        </span>
    );
}
